import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Leaf, Copy, GitMerge, EyeOff, CheckSquare, Square, RotateCcw } from "lucide-react";
import DuplicateRow from "./DuplicateRow";

/**
 * DuplicateGroup — one duplicate-group card. Owns the merge-candidate
 * math (which scans are included/excluded, who the effective primary
 * is when the default primary was unchecked, the toggle-all state)
 * because these are group-local concerns the page shouldn't have to
 * think about.
 *
 * The page still owns the action handlers (delete, merge, dismiss,
 * restore) — this component just wires them to the buttons.
 */
export default function DuplicateGroup({
  group,
  gi,
  groupKey,
  mode,
  showDismissedDivider,
  excludedSet,
  isMerging,
  busyScanId,
  language,
  locale,
  onToggleExclude,
  onToggleAll,
  onDismiss,
  onRestore,
  onRequestMerge,
  onRequestDelete,
  onOpenDetail,
}) {
  const { t } = useTranslation();

  const primary = group.scans?.[0];
  const allNonPrimary = (group.scans || []).slice(1);
  const primaryExcluded = primary && excludedSet.has(primary.scan_id);
  // If the primary itself is excluded, treat the FIRST included scan as
  // the new primary. Prevents "primary is excluded" edge cases at
  // merge time.
  const effectivePrimary =
    primary && !primaryExcluded
      ? primary
      : (group.scans || []).find((s) => !excludedSet.has(s.scan_id)) || primary;
  const effectiveMergeIds = allNonPrimary
    .filter((s) => !excludedSet.has(s.scan_id) && s.scan_id !== effectivePrimary?.scan_id)
    .map((s) => s.scan_id);
  const canMerge = !!effectivePrimary && effectiveMergeIds.length > 0;
  // Are ALL scans in this group included? (Used by the select-all toggle.)
  const allIncluded = (group.scans || []).every((s) => !excludedSet.has(s.scan_id));
  const isDismissed = !!group.dismissed;

  const groupLabel = group.common_name || group.scientific_name || "";

  return (
    <React.Fragment>
      {/* Divider between active and dismissed sections. Rendered once,
          right before the first dismissed group. */}
      {showDismissedDivider && (
        <div
          className="flex items-center gap-3 pt-2"
          data-testid="dup-dismissed-divider"
          role="separator"
          aria-label={t("duplicates.dismissedSection")}
        >
          <span className="text-[11px] uppercase tracking-[0.18em] text-leaf-mist">
            {t("duplicates.dismissedSection")}
          </span>
          <span className="flex-1 h-px bg-[#E8E5DF]" aria-hidden="true" />
        </div>
      )}

      <div
        className={`rounded-3xl bg-white border p-4 transition ${
          isDismissed
            ? "border-[#EFEBE3] bg-parchment/30 opacity-80"
            : "border-[#E8E5DF]"
        }`}
        data-testid={`dup-group-${gi}`}
        data-dismissed={isDismissed ? "true" : "false"}
      >
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-leaf-mist min-w-0 flex-wrap">
            {mode === "species"
              ? <Leaf className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
              : <Copy className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />}
            <span className="break-words">
              {mode === "species"
                ? t("duplicates.speciesGroupHeader", { name: groupLabel || "—", count: group.count })
                : t("duplicates.groupHeader", { count: group.count })}
            </span>
            {isDismissed && (
              <span
                className="ml-1 rounded-full bg-white border border-[#E8E5DF] text-leaf-mist normal-case tracking-normal px-2 py-0.5 text-[10px]"
                data-testid={`dup-dismissed-badge-${gi}`}
              >
                {t("duplicates.dismissedBadge")}
              </span>
            )}
          </div>

          {mode === "species" && primary && allNonPrimary.length > 0 && !isDismissed && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => onDismiss(group)}
                data-testid={`dup-dismiss-${gi}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-white border border-[#E8E5DF] text-leaf-ink text-xs hover:bg-parchment transition"
              >
                <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("duplicates.keepSeparate")}
              </button>
              <button
                type="button"
                onClick={() => onRequestMerge({
                  groupKey,
                  primary_scan_id: effectivePrimary.scan_id,
                  merge_ids: effectiveMergeIds,
                  label: groupLabel,
                  total: 1 + effectiveMergeIds.length,
                })}
                disabled={isMerging || !canMerge}
                data-testid={`dup-merge-${gi}`}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-leaf text-white text-xs hover:bg-leaf-deep disabled:opacity-60 transition"
              >
                {isMerging
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                  : <GitMerge className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {/* Show "Merge (N)" when user has partially selected so the
                    count is explicit. When there are no candidates left
                    (button also disabled), drop the parenthetical to
                    avoid the misleading "Merge (1)". */}
                {allIncluded || !canMerge
                  ? t("duplicates.mergeAction")
                  : t("duplicates.mergeActionN", { n: 1 + effectiveMergeIds.length })}
              </button>
            </div>
          )}

          {mode === "species" && isDismissed && (
            <button
              type="button"
              onClick={() => onRestore(group)}
              data-testid={`dup-restore-${gi}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 bg-white border border-[#E8E5DF] text-leaf-ink text-xs hover:bg-parchment transition"
              aria-label={t("duplicates.restoreAria", { name: groupLabel })}
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t("duplicates.restoreAction")}
            </button>
          )}
        </div>

        {/* Select-all row — only for species mode where merging matters.
            Hidden for dismissed groups (they're read-only preview). */}
        {mode === "species" && allNonPrimary.length > 0 && !isDismissed && (
          <div className="mt-3 flex items-center justify-between px-1">
            <button
              type="button"
              onClick={() => onToggleAll(groupKey, group, allIncluded)}
              data-testid={`dup-toggle-all-${gi}`}
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-leaf-mist hover:text-leaf-ink transition"
            >
              {allIncluded
                ? <CheckSquare className="w-3.5 h-3.5 text-leaf" strokeWidth={1.5} />
                : <Square className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {allIncluded ? t("duplicates.deselectAll") : t("duplicates.selectAll")}
            </button>
            {!allIncluded && (
              <span className="text-[11px] text-leaf-mist" data-testid={`dup-excluded-count-${gi}`}>
                {t("duplicates.excludedCount", { n: excludedSet.size })}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 space-y-2">
          {group.scans.map((s, si) => {
            const excluded = mode === "species" && excludedSet.has(s.scan_id);
            return (
              <DuplicateRow
                key={s.scan_id}
                scan={s}
                gi={gi}
                si={si}
                mode={mode}
                isDismissed={isDismissed}
                excluded={excluded}
                language={language}
                locale={locale}
                busy={busyScanId === s.scan_id}
                onToggleExclude={() => onToggleExclude(groupKey, s.scan_id)}
                onOpenDetail={() => onOpenDetail(s)}
                onDelete={(name) => onRequestDelete({ scan_id: s.scan_id, label: name })}
              />
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
}
